import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHeader } from "./About";
import { useLang } from "../contexts/LanguageContext";
import useSEO from "../hooks/useSEO";

const PHOTOS = [
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/3enovdmh_IMG_6267.JPG",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/3cug0enj_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/ruj1q5dd_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/us2xvo2q_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/hno8x6d0_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/ytpzxd2q_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/ehlysiuz_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/nd11vycm_image.png",
  "/gallery-11.jpg",
  "/gallery-12.jpg",
  "/gallery-13.jpg",
  "/gallery-14.jpg",
  "/gallery-15.jpg",
  "/gallery-16.jpg",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/n5y8hlrx_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/0g2lhr6r_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/ih3ovjdl_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/n9jp02r6_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/n1q29uls_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/1bsp0v9m_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/5dju0ass_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/jq6d9q6a_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/147eqnxl_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/pctlxz91_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/hd6uln6g_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/dw4ravek_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/66ofijhv_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/nosp6f3q_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/kra4thlb_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/a15uc6qf_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/yx7y9oxf_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/6vyylsl8_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/94geyf7n_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/kna5yvf6_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/1in2ak3h_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/hxj23mny_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/ew4i96iw_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/l73bperv_image.png",
  "/gallery-01.jpg",
  "/gallery-02.jpg",
  "/gallery-03.jpg",
  "/gallery-04.jpg",
  "/gallery-05.jpg",
  "/gallery-06.jpg",
  "/gallery-07.jpg",
  "/gallery-08.jpg",
  "/gallery-09.jpg",
  "/gallery-10.jpg",
];

const Lightbox = ({ index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-[fadeIn_.25s_ease-out]"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-6 right-6 z-10 text-white/80 hover:text-white p-2 transition-colors"
      >
        <X size={32} strokeWidth={1.5} />
      </button>

      <button
        data-testid="lightbox-prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous"
        className="absolute left-4 sm:left-8 z-10 text-white/70 hover:text-white p-3 transition-colors"
      >
        <ChevronLeft size={44} strokeWidth={1.5} />
      </button>

      <button
        data-testid="lightbox-next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next"
        className="absolute right-4 sm:right-8 z-10 text-white/70 hover:text-white p-3 transition-colors"
      >
        <ChevronRight size={44} strokeWidth={1.5} />
      </button>

      <img
        src={PHOTOS[index]}
        alt=""
        className="max-w-[92vw] max-h-[88vh] object-contain select-none"
        onClick={(e) => e.stopPropagation()}
      />

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm font-medium tracking-wide">
        {index + 1} / {PHOTOS.length}
      </div>
    </div>
  );
};

const Gallery = () => {
  const { lang } = useLang();
  const [openIdx, setOpenIdx] = useState(null);

  useSEO({
    title: lang === "en"
      ? "Gallery — Revelo Hong Kong"
      : "活動相簿 — Revelo 香港",
    description: lang === "en"
      ? "Photos from Revelo's anti-scam workshops, community outreach and learning sessions across Hong Kong."
      : "Revelo 在香港舉辦的防詐騙工作坊、社區外展及學習活動相片紀錄。",
    image: PHOTOS[1],
    lang,
  });

  const openAt = useCallback((i) => setOpenIdx(i), []);
  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(() => setOpenIdx((i) => (i - 1 + PHOTOS.length) % PHOTOS.length), []);
  const next = useCallback(() => setOpenIdx((i) => (i + 1) % PHOTOS.length), []);

  return (
    <div data-testid="gallery-page">
      <PageHeader
        eyebrow={lang === "en" ? "Gallery" : "活動相簿"}
        title={lang === "en" ? "Gallery" : "活動相簿"}
        intro={lang === "en"
          ? "Snapshots from Revelo's workshops, preparation, and community learning moments."
          : "Revelo 工作坊、活動籌備和社區學習片段的相片紀錄。"
        }
        heroImage="https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/lb704pbk_image.png"
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Large feature - first photo spans 2 cols on lg */}
              <figure
                className="md:col-span-2 lg:col-span-2 lg:row-span-2 img-parallax-wrap overflow-hidden cursor-zoom-in group"
                onClick={() => openAt(0)}
                data-testid="gallery-photo-0"
              >
                <img
                  src={PHOTOS[0]}
                  alt=""
                  className="img-parallax w-full h-[400px] lg:h-[680px] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                />
              </figure>
              {PHOTOS.slice(1).map((src, i) => (
                <figure
                  key={i}
                  className="img-parallax-wrap overflow-hidden cursor-zoom-in group"
                  onClick={() => openAt(i + 1)}
                  data-testid={`gallery-photo-${i + 1}`}
                >
                  <img
                    src={src}
                    alt=""
                    className="img-parallax w-full h-72 lg:h-80 object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </figure>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-16 bg-[#F8FAFC] border border-slate-100 p-10 lg:p-14 text-center">
            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">
              {lang === "en" ? "More moments coming soon" : "更多相片陸續更新"}
            </h3>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {lang === "en"
                ? "As our community programmes continue, we'll be adding more photos from upcoming workshops and learning sessions."
                : "隨著社區項目持續進行，我們會加入更多工作坊和學習現場嘅相片。"}
            </p>
            <Link
              to="/contact?interest=partnership"
              data-testid="gallery-cta"
              className="btn-pill mt-7 inline-flex items-center gap-2 bg-[#163B71] hover:bg-[#0A192F] text-white px-7 py-3.5 rounded-full font-bold uppercase tracking-wide transition-all"
            >
              {lang === "en" ? "Work With Us" : "與我們合作"}
              <ArrowRight size={16} className="btn-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>

      {openIdx !== null && (
        <Lightbox index={openIdx} onClose={close} onPrev={prev} onNext={next} />
      )}
    </div>
  );
};

export default Gallery;
